import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { api, SALE, ESAMI, ESAMI_EXTRA, ESAMI_JENNER, todayISO, validaCodiceFiscale } from "@/lib/clinic";
import { toast } from "sonner";
import { Loader2, UserPlus, AlertTriangle } from "lucide-react";

const VUOTO = {
  nome: "", cognome: "", azienda: "", mansione: "", tipo_visita: "",
  data_nascita: "", luogo: "", codice_fiscale: "", email: "", nazionalita: "",
  referente: "", contatto: "", orario: "", note: "",
  data: todayISO(), ambulatorio: false, visita: true, esami: {},
};

const Campo = ({ k, label, type = "text", placeholder, value, onChange, warning }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-widest text-slate-500">{label}</Label>
    <Input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={`patient-form-${k}-input`}
      className={warning ? "border-amber-400 focus-visible:ring-amber-400" : ""}
    />
    {warning && (
      <div className="flex items-start gap-1.5 text-[11px] text-amber-700" data-testid="patient-form-cf-warning">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" /> {warning}
      </div>
    )}
  </div>
);

export const AddPatientDialog = ({ open, onOpenChange, onCreated, defaultDate }) => {
  const [form, setForm] = useState(VUOTO);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const cfErrore = validaCodiceFiscale(form.codice_fiscale);

  useEffect(() => {
    if (open && defaultDate) setForm((f) => ({ ...f, data: defaultDate }));
  }, [open, defaultDate]);

  // Selezionando un esame di competenza Jenner la sala viene attivata come nel planner.
  const toggleEsame = (k) =>
    setForm((f) => {
      const esami = { ...(f.esami || {}) };
      if (esami[k]) delete esami[k];
      else esami[k] = "X";
      const serveJenner = Object.keys(esami).some((e) => ESAMI_JENNER.includes(e));
      return { ...f, esami, ambulatorio: serveJenner || f.ambulatorio };
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() && !form.azienda.trim()) {
      toast.error("Inserisci almeno il nome del paziente o l'azienda");
      return;
    }
    if (!form.ambulatorio && !form.visita) {
      toast.error("Seleziona almeno una sala");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/patients", {
        ...form,
        nome: form.nome.trim() || form.azienda.trim(),
      });
      toast.success(`${data.nome} inserito in elenco — conferma l'accettazione all'arrivo`);
      setForm({ ...VUOTO, data: form.data });
      onOpenChange(false);
      onCreated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const campo = (k, label, extra = {}) => ({
    k,
    label,
    value: form[k] || "",
    onChange: (e) => set(k)(k === "codice_fiscale" ? e.target.value.toUpperCase() : e.target.value),
    warning: k === "codice_fiscale" ? cfErrore : null,
    ...extra,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="add-patient-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Nuovo Paziente in elenco</DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            Compila tutti i dati della scheda: il codice viene generato alla conferma dell'accettazione
            oppure con la stampa anticipata del consenso.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5 pt-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Campo {...campo("nome", "Nome", { placeholder: "MARIO" })} />
            <Campo {...campo("cognome", "Cognome", { placeholder: "ROSSI" })} />
            <Campo {...campo("azienda", "Azienda", { placeholder: "AMTAB" })} />
            <Campo {...campo("mansione", "Mansione", { placeholder: "AUTISTA" })} />
            <Campo {...campo("tipo_visita", "Tipo visita", { placeholder: "PERIODICA" })} />
            <Campo {...campo("data", "Data visita", { type: "date" })} />
            <Campo {...campo("orario", "Orario (opzionale)", { type: "time" })} />
            <Campo {...campo("data_nascita", "Data di nascita", { placeholder: "01/01/1980" })} />
            <Campo {...campo("luogo", "Luogo", { placeholder: "BARI" })} />
            <Campo {...campo("codice_fiscale", "Codice fiscale")} />
            <Campo {...campo("nazionalita", "Nazionalità", { placeholder: "ITALIANA" })} />
            <Campo {...campo("email", "E-mail", { placeholder: "nome@azienda.it" })} />
            <Campo {...campo("referente", "Referente aziendale")} />
            <Campo {...campo("contatto", "Contatto (telefono)")} />
          </div>

          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-widest text-slate-500">Esami richiesti</Label>
            <div className="flex flex-wrap gap-2">
              {Object.entries({ ...ESAMI, ...ESAMI_EXTRA }).map(([k, label]) => {
                const active = !!form.esami?.[k];
                return (
                  <button
                    type="button"
                    key={k}
                    onClick={() => toggleEsame(k)}
                    data-testid={`patient-form-esame-${k}`}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                      active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                    }`}
                  >
                    {k} · {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-widest text-slate-500">Sale previste</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {["levi", "jenner"].map((r) => {
                const checked = r === "levi" ? form.visita : form.ambulatorio;
                return (
                  <label
                    key={r}
                    className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${
                      checked ? "border-sky-400 bg-sky-50/70" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => set(r === "levi" ? "visita" : "ambulatorio")(!!v)}
                      data-testid={`patient-form-${r}-checkbox`}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block font-display text-base font-semibold leading-tight text-slate-900">
                        {SALE[r].label}
                      </span>
                      <span className="block text-xs text-slate-500 mt-1">{SALE[r].desc}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-widest text-slate-500">Note</Label>
            <Textarea
              rows={2}
              value={form.note}
              onChange={(e) => set("note")(e.target.value)}
              placeholder="Indicazioni per l'accettazione o per il medico"
              data-testid="patient-form-note-input"
            />
          </div>

          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="patient-form-submit-btn" className="w-full sm:w-auto rounded-full px-6">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Aggiungi in elenco
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
